import { MenuItem } from "@chakra-ui/react";
import React from "react";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import * as GQL from "src/types/graphql";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canMakeOffers } from "../validations/makeOffers";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkOffer } from "./useEnqueueBulkOffer";
import {
  ConfirmationResult,
  useMakeOfferConfirmationDialog,
} from "./useMakeOfferConfirmationDialog";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
}

export const BulkMakeOffer = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
}: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkOffer = useEnqueueBulkOffer();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const { confirm, confirmationDialog } = useMakeOfferConfirmationDialog(
    selectedFormSchoolStatuses
  );

  const handleOnClick = React.useCallback(async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "MAKE_OFFER",
      bulk: true,
      count,
      async: true,
    });
    const confirmationResult: ConfirmationResult = await confirm(
      selectedFormSchoolStatuses,
      count
    );
    if (confirmationResult.result === "Cancelled") {
      trackFormAction({
        verb: "Canceled",
        actionType: "MAKE_OFFER",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "MAKE_OFFER",
      bulk: true,
      count,
      async: true,
      extra: { program_id: confirmationResult.programId },
    });
    await enqueueBulkOffer({
      actionType: "MAKE_OFFER",
      actionData: { program_id: confirmationResult.programId },
    });
  }, [
    bulk.selectionSize,
    confirm,
    enqueueBulkOffer,
    selectedFormSchoolStatuses,
  ]);

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canMakeOffers(
            selectedFormSchoolStatuses,
            selectedFormSchools,
            (subStatus) =>
              isSubStatusTransitionAllowed(
                subStatus,
                GQL.offer_status_enum.Offered
              )
          )
        }
      >
        Make offers
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
